import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { enviroment } from 'src/environments/environment';
import { AccountDeletionRequest, AccountDeletionStatus } from '../model/account-deletion';
import { LoginResponse } from '../model/response/auth-response';

/** Rota da tela de desistência. Fica fora do shell: qualquer outra chamada da API devolve 403. */
export const PENDING_DELETION_ROUTE = '/account/pending-deletion';

/**
 * Chave do estado "conta em carência". Só sessionStorage, de propósito: não entra em
 * AUTH_STORAGE_KEYS (não é espelhada no localStorage), então uma aba nova não herda o flag e
 * descobre pelo 403 do interceptor. O logout() limpa o sessionStorage e leva o flag junto.
 */
const PENDING_KEY = 'pendingDeletionAt';

@Injectable({ providedIn: 'root' })
export class AccountDeletionService {

  constructor(private http: HttpClient, private router: Router) {}

  private url(userId: string): string {
    return `${enviroment.BASE_URL_API}/user/${userId}/deletion`;
  }

  request(userId: string, confirmationEmail: string): Observable<AccountDeletionStatus> {
    const body: AccountDeletionRequest = { confirmationEmail };
    return this.http.post<AccountDeletionStatus>(this.url(userId), body);
  }

  getStatus(userId: string): Observable<AccountDeletionStatus> {
    return this.http.get<AccountDeletionStatus>(this.url(userId));
  }

  cancel(userId: string): Observable<AccountDeletionStatus> {
    return this.http.delete<AccountDeletionStatus>(this.url(userId));
  }

  /**
   * Registra o estado da conta a partir de uma resposta de login. Só olha respostas que
   * trazem tokens: o desafio de MFA e o REGISTRATION_REQUIRED vêm com 'active' por padrão, e
   * o refresh não traz o campo — usá-los apagaria um flag válido.
   */
  applyLoginResponse(response: LoginResponse): void {
    if (!response.authenticationResult) return;
    if (response.accountStatus === 'pending_deletion') {
      this.markPending(response.deletionScheduledAt ?? null);
    } else {
      this.clearPending();
    }
  }

  /** `scheduledAt` nulo vira '' — o flag existe, mas a data ainda não é conhecida. */
  markPending(scheduledAt: string | null): void {
    try {
      sessionStorage.setItem(PENDING_KEY, scheduledAt ?? '');
    } catch { /* storage indisponível: o 403 do interceptor cobre */ }
  }

  clearPending(): void {
    try {
      sessionStorage.removeItem(PENDING_KEY);
    } catch { /* idem */ }
  }

  get isPending(): boolean {
    try {
      return sessionStorage.getItem(PENDING_KEY) !== null;
    } catch {
      return false;
    }
  }

  /** Data de fim da carência guardada no flag, quando já conhecida. */
  get pendingScheduledAt(): string | null {
    try {
      return sessionStorage.getItem(PENDING_KEY) || null;
    } catch {
      return null;
    }
  }

  /**
   * Leva para a tela de desistência, a não ser que já esteja nela — várias chamadas podem
   * dar 403 ao mesmo tempo e não devem gerar uma tempestade de navegações.
   */
  goToPendingPage(): Promise<boolean> {
    if (this.router.url.split(/[?#]/)[0] === PENDING_DELETION_ROUTE) {
      return Promise.resolve(true);
    }
    return this.router.navigate([PENDING_DELETION_ROUTE]);
  }
}
